import '../../../../Frontend/js/util/general';
import Hls from "hls.js";

// Ensure global JVM exists even if side-effect imports are tree-shaken in prod builds.
if (!window.JVM) {
  window.JVM = {
    YouTube: null,
    BlockMediaScriptIsInitialized: false
  };
}

let mediaEventsAreBound = false;

export const useBlockMedia = () => {
  const hasMarketingConsent = () => window.Cookiebot?.consent?.marketing === true;

  const initialize = () => {
    initializeHlsVideos();
    initializeCookieSettingsLinks();
    initializeYouTubeConsentState();
    initializeDirectVideoConsentState();
    bindMediaEvents();

    // Tell other modules that this script is loaded
    document.dispatchEvent(new CustomEvent("YouTubeRelatedScriptLoaded"));

    initializeStreamio();
    initializeHtml5Video();

    window.JVM.BlockMediaScriptIsInitialized = true;
  }

  const initializeCookieSettingsLinks = () => {
    document.querySelectorAll<HTMLElement>(".video-block__cookie-settings-link").forEach(element => {
      if (element.dataset.initializedCookieSettingsLink === "true") {
        return;
      }

      element.addEventListener("click", e => {
        e.preventDefault();
        e.stopPropagation();
        window.Cookiebot?.renew?.();
      });

      element.dataset.initializedCookieSettingsLink = "true";
    });
  }

  const bindMediaEvents = () => {
    if (mediaEventsAreBound) {
      return;
    }

    document.addEventListener('YouTubeScriptLoaded', function(e) {
      initializeYouTubeConsentState();

      if (hasMarketingConsent() && !!window.JVM.YouTube) {
        window.JVM.YouTube.initialize();
      }
    });

    // When CookieBot consents are changed
    window.addEventListener('CookiebotOnAccept', function(e) {
      initializeYouTubeConsentState();
      document.dispatchEvent(new CustomEvent("YouTubeRelatedScriptLoaded"));
      initializeStreamio();
      initializeDirectVideoConsentState();
    });

    mediaEventsAreBound = true;
  }

  const showConsentMessage = (blockElement: HTMLElement) => {
    blockElement.classList.add("video-block__content--show-video");
    blockElement.classList.add("video-block__content--video-started");
  }

  const initializeConsentState = (blockElement: HTMLElement) => {
    blockElement.classList.add("video-block__content--prevent-video");

    if (blockElement.dataset.initializedConsent === "true") {
      return;
    }

    blockElement.addEventListener("click", () => {
      showConsentMessage(blockElement);
    });

    blockElement.addEventListener("keydown", (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        showConsentMessage(blockElement);
      }
    });

    blockElement.dataset.initializedConsent = "true";
  }

  const allowMediaState = (blockElement: HTMLElement, initializedAttribute: string) => {
    if (blockElement.dataset[initializedAttribute] === "true") {
      return false;
    }

    blockElement.classList.remove("video-block__content--prevent-video");
    blockElement.classList.remove("video-block__content--show-video");
    blockElement.classList.remove("video-block__content--video-started");
    blockElement.dataset[initializedAttribute] = "true";

    return true;
  }

  const initializeYouTubeConsentState = () => {
    document.querySelectorAll<HTMLElement>(".video-block__content--youtube").forEach(blockElement => {
      if (!hasMarketingConsent() || !window.JVM.YouTube) {
        initializeConsentState(blockElement);
        return;
      }

      allowMediaState(blockElement, "initializedMedia");
    });
  }

  const initializeDirectVideoConsentState = () => {
    document.querySelectorAll<HTMLVideoElement>(".video-block__html5-video[data-video-src]").forEach(videoElement => {
      const blockElement = videoElement.closest<HTMLElement>(".video-block__content--html5");

      if (!blockElement) {
        return;
      }

      if (!hasMarketingConsent()) {
        initializeConsentState(blockElement);
        return;
      }

      if (!allowMediaState(blockElement, "initializedDirectVideo")) {
        return;
      }

      const videoSrc = videoElement.dataset.videoSrc;
      if (!videoSrc) {
        return;
      }

      videoElement.src = videoSrc;
      videoElement.hidden = false;
      videoElement.load();
      initializeHlsVideo(videoElement);
    });
  }

  // Add a YT-player for every media block
  const initializeMediaBlocks = () => {
    document.querySelectorAll(".video-block__content--youtube").forEach(element => {
      let blockElement = element as HTMLElement;

      // If this script is already initialized
      if (blockElement.dataset.initializedMediaBlockEvents === "true") {
        return;
      }

      blockElement.addEventListener("click", () => {
        initializeVideoAndPlay(blockElement);
      });

      blockElement.dataset.initializedMediaBlockEvents = "true";
    });
  }

  const initializeVideoAndPlay = (blockElement: Element) => {
    if (blockElement instanceof HTMLElement && !!window.JVM.YouTube && hasMarketingConsent()) {
      if (!blockElement.querySelector("iframe")) {
        blockElement?.classList.remove("video-block__content--show-video");
        blockElement.classList.add("video-block__content--load-video");
        const {videoid, height, width, ytplayerid} = blockElement.dataset;
        window.JVM.YouTube.createPlayer(height, width, videoid, ytplayerid, onPlayerStateChange);
      } else {
        window.JVM.YouTube.playYouTubeVideo(blockElement);
      }
    }
  }

  const initializeHlsVideos = function () {
    if (Hls.isSupported()) {
      const elements = document.querySelectorAll('video');

      elements.forEach(element => {
        initializeHlsVideo(element);
      });
    }
  }

  const initializeHlsVideo = (videoElement: HTMLVideoElement) => {
    const src = videoElement.currentSrc || videoElement.src;

    if (!Hls.isSupported() || !src || !new URL(src, window.location.href).pathname.toLowerCase().endsWith(".m3u8")) {
      return;
    }

    const hls = new Hls({maxBufferLength: 3, maxBufferSize: 20 * 1000 * 1000});
    hls.loadSource(src);
    hls.attachMedia(videoElement);
  }

  document.addEventListener("DOMContentLoaded", function () {
    var isViewPagePartial = document.querySelector('.view-page, .block-partial');

    if (!!isViewPagePartial) {
      initialize();
    }
  });

  const onPlayerStateChange = (event: any, ytPlayerId: string | undefined) => {
    let blockElement = document.querySelector(`#${ytPlayerId}`)?.closest(".video-block__content");

    if (event.data == window.YT.PlayerState.PAUSED) {
      blockElement?.classList.remove("video-block__content--show-video");
    } else if (event.data == window.YT.PlayerState.PLAYING) {
      blockElement?.classList.remove("video-block__content--load-video");
      blockElement?.classList.add("video-block__content--show-video");
      blockElement?.classList.add("video-block__content--video-started");
    }
  }

  const initializeStreamio = () => {
    document.querySelectorAll<HTMLElement>(".video-block__content--streamio").forEach(element => {
      const iframe = element.querySelector("iframe");

      if (!!iframe) {
        if (!hasMarketingConsent()) {
          initializeConsentState(element);
          return;
        }

        if (element.dataset.initializedStreamio === "true") {
          return;
        }

        element.classList.remove("video-block__content--prevent-video");
        element.classList.remove("video-block__content--show-video");
        element.classList.remove("video-block__content--video-started");

        const iframeSrcValue = iframe.getAttribute("video-src");
        iframe.removeAttribute("video-src");
        iframe.setAttribute("src", iframeSrcValue || '');

        element.addEventListener("click", () => {
          if (iframe) {
            element.classList.add("video-block__content--load-video");

            iframe.contentWindow?.postMessage({
              action: 'play'
            }, '*');

            element.classList.add("video-block__content--show-video");
            element.classList.add("video-block__content--video-started");
            element.classList.remove("video-block__content--load-video");
          }
        });

        element.dataset.initializedStreamio = "true";
      }
    });
  }

  const initializeHtml5Video = () => {
    document.querySelectorAll<HTMLElement>(".video-block__content--html5").forEach(element => {
      const videoElement = element.querySelector(".video-block__html5-video") as HTMLVideoElement;
      const playButton = element.querySelector(".video-block__play-button") as HTMLButtonElement;
      const overlay = element.querySelector(".video-block__image-overlay") as HTMLElement;

      if (!videoElement || !playButton) return;

      if (element.dataset.initializedHtml5Video === "true") return;
      element.dataset.initializedHtml5Video = "true";

      videoElement.setAttribute("playsinline", "");
      videoElement.setAttribute("webkit-playsinline", "");

      if (overlay) {
        overlay.classList.add("video-block__image-overlay--clickcatcher");
      }

      let hasStarted = false;

      const showVideoUI = () => {
        element.classList.add("video-block__content--show-video");
        element.classList.add("video-block__content--video-started");

        if (overlay) overlay.classList.add("is-hidden");

        playButton.style.display = "none";

        // Enable controls after first start
        videoElement.controls = true;
      };

      const startVideo = async () => {
        if (videoElement.dataset.requiresMarketingConsent === "true" && !hasMarketingConsent()) {
          showConsentMessage(element);
          return;
        }

        try {
          if (!hasStarted) {
            videoElement.currentTime = 0;
            hasStarted = true;
            showVideoUI();
          }

          await videoElement.play();
        } catch (err) {
          console.warn(err);
        }
      };

      const toggleVideo = async () => {
        if (videoElement.paused) {
          await startVideo();
        } else {
          videoElement.pause();
        }
      };

      // Play button
      playButton.addEventListener("click", async (e) => {
        e.preventDefault();
        e.stopPropagation();
        await startVideo();
      });

      // Overlay
      if (overlay) {
        overlay.addEventListener("click", async (e) => {
          e.preventDefault();
          e.stopPropagation();
          await toggleVideo();
        });
      }

      element.addEventListener("click", async (e: MouseEvent) => {
        const target = e.target as HTMLElement;

        if (target.closest("a")) return;
        if (target.closest("video")) return;
        if (target.closest(".video-block__play-button")) return;
        if (target.closest(".video-block__image-overlay")) return;

        e.preventDefault();
        await toggleVideo();
      });

      videoElement.addEventListener("pause", () => {
        playButton.style.display = "block";
      });

      videoElement.addEventListener("playing", () => {
        playButton.style.display = "none";
      });
    });
  };

  return {
    initialize,
    initializeMediaBlocks,
    onPlayerStateChange,
  }
}

// Iframe has loaded
window.iframeLoadedHandler = (iframeElement: HTMLIFrameElement) => {
  const element = iframeElement.closest(".video-block__content") as HTMLElement;
  element.classList.add("video-block__content--show-video");
  element.classList.add("video-block__content--video-started");
  element.classList.remove("video-block__content--load-video");
};

// This method is called from YouTube-api
window.onYouTubeIframeAPIReady = () => {
  useBlockMedia().initializeMediaBlocks();
}

window.CookiebotCallback_OnLoad = () => {
  if (!window.JVM.BlockMediaScriptIsInitialized) {
    useBlockMedia().initialize();
  }
};

// When loaded dynamically after page load
if (window.Cookiebot?.loaded) {
  window.JVM.BlockMediaScriptIsInitialized = false;
  window.YT = null;

  if (window.JVM.YouTube != null) {
    window.JVM.YouTube.initialized = false;
  }

  useBlockMedia().initialize();
}
